import type { UtilityContext } from '@change-corgi/core/react/utilityContext';

import type { AiSummaries } from './aiSummaries';
import { getAiSummaries } from './aiSummaries';
import { isDecisionMaker } from './decisionMaker';
import { fetchPetitionDetailsExperiments } from './experiments';
import type { PetitionDetailsDataExperimentsQuery } from './experiments.graphql';
import { isGdprConsentProvided } from './gdprConsentProvided';
import { normalizeSupporterGrowthEmbedSignPetitionFormExperiment } from './normalize/normalizeSupporterGrowthEmbedSignPetitionFormExperiment';
import type { PetitionViewerInfo } from './petitionViewerInfo';
import { getPetitionViewerInfo } from './petitionViewerInfo';
import { getRecentUserPetition } from './recentUserPetition';

type Experiments = PetitionDetailsDataExperimentsQuery['experiments'];
type SupporterGrowthEmbedSignPetitionFormExperiment = Experiments['supporterGrowthEmbedSignPetitionFormExperiment'];
export type SupporterGrowthEmbedSignPetitionFormExperimentVariation =
	SupporterGrowthEmbedSignPetitionFormExperiment['variation'];

export type PetitionDetailsPageUserData = PetitionViewerInfo & {
	userId: string | undefined;
	aiSummaries: AiSummaries | null;
	decisionMaker: boolean;
	gdprConsentProvided: boolean;
	signatureThermometerEnabled: boolean;
	isSunriseEnabled: boolean;
	fullSunrisePetitionPageShareComponentExperiment: boolean;
	fullSunrisePetitionHeaderExperiment: 'control' | 'fake_control' | 'variant_a' | 'variant_b';
	fullSunriseActionPanelExperimentVariation: string;
	petitionPageMomentumBarExperiment: 'control' | 'enabled';
	supporterGrowthEmbedSignPetitionFormExperimentVariation: SupporterGrowthEmbedSignPetitionFormExperimentVariation;
	starterDashboardPromptingBannerEnabled: boolean;
	experiments: Omit<
		Experiments,
		| 'sunriseSupportersV1'
		| 'supporterShareVisualsExperiment'
		| 'signatureThermometerExperimentDesktop'
		| 'fullSunrisePetitionPageShareComponentExperiment'
		| 'fullSunrisePetitionHeaderExperiment'
		| 'actionPanelSunriseExperiment'
		| 'petitionPageMomentumBarExperiment'
		| 'supporterGrowthEmbedSignPetitionFormExperiment'
	>;
};

async function normalizeExperiments(utilityContext: UtilityContext, experiments: Experiments) {
	return {
		supporterGrowthEmbedSignPetitionFormExperimentVariation:
			await normalizeSupporterGrowthEmbedSignPetitionFormExperiment({
				experiment: experiments.supporterGrowthEmbedSignPetitionFormExperiment,
				utilityContext,
			}),
	};
}

export async function getPetitionDetailsPageUserData(
	utilityContext: UtilityContext,
	slugOrId: string,
	{
		currentUserId,
		isGuest,
		decisionMakerHash,
	}: {
		currentUserId: string | undefined;
		isGuest: boolean;
		decisionMakerHash?: string;
		hasShowLegislativeBodiesParam: boolean;
	},
): Promise<PetitionDetailsPageUserData> {
	const [
		{ petitionId, ...petitionViewerInfo },
		aiSummaries,
		decisionMaker,
		gdprConsentProvided,
		recentPetition,
		experiments,
	] = await Promise.all([
		getPetitionViewerInfo(utilityContext, slugOrId, currentUserId),
		getAiSummaries(utilityContext, { slugOrId }),
		!isGuest || decisionMakerHash
			? isDecisionMaker(utilityContext, { slugOrId, decisionMakerHash })
			: Promise.resolve(false),
		isGdprConsentProvided(utilityContext),
		isGuest ? Promise.resolve(undefined) : getRecentUserPetition(utilityContext),
		fetchPetitionDetailsExperiments(utilityContext),
	]);

	const {
		sunriseSupportersV1,
		signatureThermometerExperimentDesktop,
		fullSunrisePetitionPageShareComponentExperiment,
		fullSunrisePetitionHeaderExperiment,
		actionPanelSunriseExperiment,
		petitionPageMomentumBarExperiment,
		...experimentsRest
	} = experiments;

	const normalizedEmbeddedSignFormExperiment = await normalizeExperiments(utilityContext, experiments);

	return {
		...petitionViewerInfo,
		...normalizedEmbeddedSignFormExperiment,
		userId: currentUserId,
		aiSummaries,
		decisionMaker,
		gdprConsentProvided,
		signatureThermometerEnabled: signatureThermometerExperimentDesktop.variation === 'v1',
		isSunriseEnabled: sunriseSupportersV1.variation === 'enabled',
		fullSunrisePetitionPageShareComponentExperiment:
			fullSunrisePetitionPageShareComponentExperiment.variation === 'enabled',
		fullSunrisePetitionHeaderExperiment: fullSunrisePetitionHeaderExperiment.variation,
		fullSunriseActionPanelExperimentVariation: actionPanelSunriseExperiment.variation,
		petitionPageMomentumBarExperiment: petitionPageMomentumBarExperiment.variation,
		starterDashboardPromptingBannerEnabled: isStarterDashboardPromptingBannerEnabled({
			petitionId,
			ownPetition: petitionViewerInfo.ownPetition,
			recentPetition,
		}),
		experiments: experimentsRest,
	};
}

function isStarterDashboardPromptingBannerEnabled({
	petitionId,
	ownPetition,
	recentPetition,
}: {
	petitionId?: string;
	ownPetition: boolean;
	recentPetition: { id: string } | undefined;
}) {
	if (!recentPetition || !petitionId) return false;
	if (!ownPetition) return false;
	if (recentPetition.id !== petitionId) return false;
	return true;
}
